import { CommonModule } from '@angular/common';
import { Component, Input, ViewChild, ElementRef, AfterViewInit, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { TagModule, Tag } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { EMPLOYEE_STATUS_CONFIG } from '@shared/enums/employee-status.enum';
import { EMPLOYEE_POSITION_STATUS_CONFIG } from '@shared/enums/employee-position-status.enum';
import { PROBATION_STATUS_CONFIG } from '@shared/enums/probation-status.enum';
import { ROOM_STATUS_CONFIG } from '@shared/enums/room-status.enum';
import { STUDY_PLAN_STATUS_CONFIG, SKILL_CONFIG } from '@shared/enums/study-plan.enum';
import { SESSION_STATUS_CONFIG } from '@shared/enums/session-status.enum';
import { TICKET_TYPE_CONFIG, TICKET_STATUS_CONFIG } from '@shared/enums/ticket.enum';
import { COURSE_TYPE_CONFIG } from '@shared/enums/course-type.enum';
import { COURSE_STATUS_CONFIG } from '@shared/enums/course-status.enum';
import { EXTENDED_LEARNER_EVENT_TYPE_CONFIG } from '@shared/enums/learner-event-type.enum';
import { LEARNER_STATUS_CONFIG } from '@shared/enums/learner.enum';

export const TEST_TYPE_CONFIG: Record<string, { label: string; cssClass: string }> = {
    INDEPENDENT: { label: 'Thi độc lập', cssClass: 'text-sm !bg-orange-100 !text-orange-700' },
    RETAKE: { label: 'Thi bù/lại', cssClass: 'text-sm !bg-red-100 !text-red-700' },
    MAIN: { label: 'Thi chính thức', cssClass: 'text-sm !bg-green-100 !text-green-700' }
};

export type TagType = 'employeeStatus' | 'employeePositionStatus' | 'probationStatus' | 'roomStatus' | 'studyPlanStatus' | 'skill' | 'sessionStatus' | 'ticketType' | 'ticketStatus' | 'courseType' | 'courseStatus' | 'learnerEventType' | 'testType' | 'learnerStatus';

interface TagConfig {
    label: string;
    cssClass: string;
}

@Component({
    selector: 'generic-p-tag',
    standalone: true,
    imports: [
        CommonModule,
        TagModule,
        TooltipModule
    ],
    template: `
        <p-tag #tagElement
               [value]="tagLabel"
               [styleClass]="convertedStyleClass"
               [style]="customStyle"
               [pTooltip]="shouldShowTooltip ? tagLabel : ''"
               tooltipPosition="top">
        </p-tag>
    `
})
export class GenericTagComponent implements OnChanges, AfterViewInit {
    @Input() value!: string;
    @Input() type?: TagType;
    @Input() styleClass?: string; // Additional CSS classes
    @Input() tagColor?: string; // Custom background color
    @Input() tagTextColor?: string; // Custom text color

    @ViewChild('tagElement', { static: false }) tagElement!: Tag;

    shouldShowTooltip = false;
    tagLabel: string = '';
    convertedStyleClass: string = '';
    customStyle: any = {};

    private readonly statusConfigs = {
        employeeStatus: EMPLOYEE_STATUS_CONFIG,
        employeePositionStatus: EMPLOYEE_POSITION_STATUS_CONFIG,
        probationStatus: PROBATION_STATUS_CONFIG,
        roomStatus: ROOM_STATUS_CONFIG,
        studyPlanStatus: STUDY_PLAN_STATUS_CONFIG,
        skill: SKILL_CONFIG,
        sessionStatus: SESSION_STATUS_CONFIG,
        ticketType: TICKET_TYPE_CONFIG,
        ticketStatus: TICKET_STATUS_CONFIG,
        courseType: COURSE_TYPE_CONFIG,
        courseStatus: COURSE_STATUS_CONFIG,
        learnerEventType: EXTENDED_LEARNER_EVENT_TYPE_CONFIG,
        testType: TEST_TYPE_CONFIG,
        learnerStatus: LEARNER_STATUS_CONFIG
    };

    ngOnChanges(changes: SimpleChanges): void {
        // Update tag display when inputs change
        if (changes['value'] || changes['type'] || changes['styleClass'] || changes['tagColor'] || changes['tagTextColor']) {
            this.updateTag();
        }
    }

    ngAfterViewInit(): void {
        this.updateTag();
    }

    private updateTag(): void {
        // Update display properties
        const config = this.getConfig();
        const label = config?.label || this.value;

        // Handle styling based on priority: custom colors > enum config > default
        if (this.tagColor || this.tagTextColor) {
            // Custom colors provided - use minimal CSS classes and apply colors via style
            this.convertedStyleClass = this.styleClass ? `text-sm ${this.styleClass}` : 'text-sm';
            this.customStyle = {
                ...(this.tagColor && { 'background-color': this.tagColor }),
                ...(this.tagTextColor && { color: this.tagTextColor })
            };
        } else {
            // Use enum config or default styling
            const baseCssClass = config?.cssClass || 'text-sm !bg-gray-100 !text-gray-700';
            this.convertedStyleClass = this.styleClass ? `${baseCssClass} ${this.styleClass}` : baseCssClass;
            this.customStyle = {};
        }

        this.tagLabel = label;

        // Check overflow after DOM updates, but only if view is initialized
        if (this.tagElement) {
            setTimeout(() => {
                this.checkOverflow();
            });
        }
    }

    private checkOverflow(): void {
        if (this.tagElement?.el?.nativeElement) {
            const element = this.tagElement.el.nativeElement;
            const labelElement = element.querySelector('.p-tag-label') as HTMLElement;
            const style = getComputedStyle(element);

            if (labelElement) {
                // Store original styles
                const originalOverflow = labelElement.style.overflow;
                const originalTextOverflow = labelElement.style.textOverflow;
                const originalWhiteSpace = labelElement.style.whiteSpace;

                // Temporarily remove overflow restrictions to get true content width
                labelElement.style.overflow = 'visible';
                labelElement.style.textOverflow = 'unset';
                labelElement.style.whiteSpace = 'nowrap';

                // Get the actual content width and available width
                const contentWidth = labelElement.scrollWidth;
                const paddingLeft = parseFloat(style.paddingLeft);
                const paddingRight = parseFloat(style.paddingRight);
                const availableWidth = element.clientWidth - paddingLeft - paddingRight;

                // Restore original styles
                labelElement.style.overflow = originalOverflow;
                labelElement.style.textOverflow = originalTextOverflow;
                labelElement.style.whiteSpace = originalWhiteSpace;

                // Check if content overflows
                this.shouldShowTooltip = contentWidth > availableWidth;
            }
        } else {
            console.log('Tag element not found');
        }
    }

    private getConfig(): TagConfig | undefined {
        if (!this.type) {
            return undefined;
        }
        const statusConfig = this.statusConfigs[this.type];
        if (!statusConfig) {
            return undefined;
        }
        return (statusConfig as any)[this.value];
    }
}
